import { Component, AfterViewInit, inject, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { LoaderComponent } from './shared/components/loader/loader.component';
import { ModalHandlerService } from './core/services/modal-handler.service';
import { AuthService } from '@auth0/auth0-angular';
import { UserPermissionInitializerService } from './core/services/user-permission-initializer.service';
import { ActiveOrganisationService } from './core/services/activeOrganisation.service';
import { take } from 'rxjs';
@Component({
  selector: 'app-root',
  imports: [RouterOutlet, LoaderComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.less',
})
export class AppComponent implements OnInit, AfterViewInit {
  colorStyles: {
    className: string;
    property: 'color' | 'background-color' | 'border-color';
    value: string;
  }[] = [];

  //Handel popup's get closed on navigation
  public modalHandlerService = inject(ModalHandlerService);
  private auth = inject(AuthService);
  private permissionService = inject(UserPermissionInitializerService);
  private activeOrganisationService = inject(ActiveOrganisationService);

  ngOnInit(): void {
    // Restore previously selected organisation context on page reload
    this.activeOrganisationService.initialize().catch((error) => {
      console.error('Failed to initialize organisation context:', error);
    });

    // Load permissions on page reload if user is authenticated
    this.auth.isAuthenticated$.pipe(take(1)).subscribe(async (isAuth) => {
      if (isAuth) {
        try {
          await this.permissionService.loadPermissions(true);
        } catch (error) {
          console.error('Failed to load permissions on page reload:', error);
        }
      }
    });
  }

  ngAfterViewInit(): void {
    // Initial scan
    this.scanAndApplyColors();

    // Watch DOM changes forever
    this.startDomWatcher();
  }

  private scanAndApplyColors() {
    this.colorStyles = this.extractColorStyleFromDOM();
    this.applyExtractedStyles();
  }

  /** Works for text-xxxxxx / bg-xxxxxx / border-xxxxxx */
  private extractColorStyleFromDOM() {
    const output: any[] = [];

    document.querySelectorAll<HTMLElement>('*').forEach((el) => {
      el.classList.forEach((cls) => {
        const match = cls.match(/^(text|bg|border)-([0-9A-Fa-f]{6})$/);
        if (!match) return;

        const [, type, hex] = match;

        const property =
          type === 'text'
            ? 'color'
            : type === 'bg'
              ? 'background-color'
              : 'border-color';

        output.push({
          className: cls,
          property,
          value: `#${hex}`,
        });
      });
    });

    // Deduplicate
    const map = new Map();
    output.forEach((o) => map.set(o.className, o));
    return Array.from(map.values());
  }

  /** Build CSS */
  private applyExtractedStyles(): void {
    const css = this.colorStyles
      .map(({ className, property, value }) => {
        const safe = CSS.escape(className);
        return `.${safe} { ${property}: ${value} }`;
      })
      .join('\n');

    let styleEl = document.getElementById(
      'dynamic-color-styles'
    ) as HTMLStyleElement;
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'dynamic-color-styles';
      document.head.appendChild(styleEl);
    }

    styleEl.innerHTML = css;
  }

  /** Watches router changes, *ngIf content, lazy modules */
  private startDomWatcher() {
    const observer = new MutationObserver(() => {
      this.scanAndApplyColors();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
  }
}

